package com.rosario.juridico.ui.screens

import com.rosario.juridico.ui.components.CajaResultadoAccionable
import androidx.compose.animation.*
import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import com.rosario.juridico.ui.components.WebLazyColumnScrollbar
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.automirrored.filled.Help
import androidx.compose.material.icons.filled.*
import androidx.compose.material.icons.outlined.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.rotate
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.navigation.NavController
import com.rosario.juridico.domain.AppUsageManager
import com.rosario.juridico.navigation.Routes
import com.rosario.juridico.openUrl
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch

@OptIn(ExperimentalMaterial3Api::class, ExperimentalLayoutApi::class)
@Composable
fun PantallaCodigosSellados(navController: NavController) {
    var busqueda by rememberSaveable { mutableStateOf("") }
    var listaOriginal by remember { mutableStateOf<List<SelladoItem>>(emptyList()) }
    var isLoading by remember { mutableStateOf(true) }
    var errorCarga by remember { mutableStateOf(false) }
    var expandidosSet by rememberSaveable { mutableStateOf(setOf<String>()) }

    LaunchedEffect(Unit) {
        try {
            val items = SelladosRepository.getItems()
            if (items.isEmpty()) {
                errorCarga = true
            } else {
                listaOriginal = items
            }
        } catch (e: Exception) {
            errorCarga = true
        } finally {
            isLoading = false
        }
    }

    val resultadosFiltrados = remember(busqueda, listaOriginal) {
        val query = busqueda.normalized().trim()
        if (query.isEmpty()) {
            listaOriginal
        } else {
            listaOriginal.filter { item ->
                item.nombre.normalized().contains(query) ||
                item.codigo.contains(query) ||
                item.observaciones.normalized().contains(query) ||
                item.norma.normalized().contains(query)
            }
        }
    }

    Scaffold(
        topBar = {
            Surface(
                color = MaterialTheme.colorScheme.surface,
                tonalElevation = 4.dp,
                shadowElevation = 4.dp
            ) {
                Column(modifier = Modifier.padding(top = 8.dp)) {
                    Row(
                        modifier = Modifier.fillMaxWidth().padding(horizontal = 8.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        IconButton(onClick = { navController.popBackStack() }) {
                            Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = "Volver")
                        }
                        Column(modifier = Modifier.weight(1f)) {
                            Text(
                                "Calculadora de Sellados",
                                style = MaterialTheme.typography.titleLarge,
                                fontWeight = FontWeight.Bold
                            )
                            Text(
                                "Santa Fe - Versión Oficial 2026.3",
                                style = MaterialTheme.typography.labelSmall,
                                color = MaterialTheme.colorScheme.primary
                            )
                        }
                    }
                    
                    OutlinedTextField(
                        value = busqueda,
                        onValueChange = { busqueda = it },
                        placeholder = { Text("Código, nombre o palabra clave...") },
                        leadingIcon = { Icon(Icons.Default.Search, contentDescription = null, tint = MaterialTheme.colorScheme.primary) },
                        trailingIcon = {
                            if (busqueda.isNotEmpty()) {
                                IconButton(onClick = { busqueda = "" }) {
                                    Icon(Icons.Default.Clear, contentDescription = "Limpiar")
                                }
                            }
                        },
                        modifier = Modifier.fillMaxWidth().padding(16.dp),
                        shape = RoundedCornerShape(12.dp),
                        singleLine = true
                    )
                }
            }
        }
    ) { padding ->
        Box(modifier = Modifier.fillMaxSize().padding(padding).background(MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.1f))) {
            if (isLoading) {
                CircularProgressIndicator(modifier = Modifier.align(Alignment.Center))
            } else if (errorCarga) {
                PantallaErrorCarga()
            } else {
                val lazyListState = rememberLazyListState()
                WebLazyColumnScrollbar(state = lazyListState, modifier = Modifier.fillMaxSize()) {
                    LazyColumn(
                        state = lazyListState,
                        modifier = Modifier.fillMaxSize(),
                        contentPadding = PaddingValues(16.dp),
                        verticalArrangement = Arrangement.spacedBy(12.dp),
                    ) {
                        if (resultadosFiltrados.isEmpty()) {
                            item {
                                Column(
                                    modifier = Modifier.fillMaxWidth().padding(vertical = 40.dp),
                                    horizontalAlignment = Alignment.CenterHorizontally
                                ) {
                                    Icon(Icons.Outlined.SearchOff, null, modifier = Modifier.size(64.dp), tint = MaterialTheme.colorScheme.outline)
                                    Spacer(Modifier.height(16.dp))
                                    Text(
                                        "Sin coincidencias en la base primaria",
                                        style = MaterialTheme.typography.titleMedium,
                                        color = MaterialTheme.colorScheme.onSurface
                                    )
                                    Spacer(Modifier.height(8.dp))
                                    Text(
                                        "¿No encontrás el código? Consultá al asistente IA",
                                        style = MaterialTheme.typography.bodySmall,
                                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                                        textAlign = TextAlign.Center
                                    )
                                    Spacer(Modifier.height(16.dp))
                                    Button(
                                        onClick = { navController.navigate("chat_ai_screen") },
                                        colors = ButtonDefaults.buttonColors(containerColor = MaterialTheme.colorScheme.secondaryContainer, contentColor = MaterialTheme.colorScheme.onSecondaryContainer)
                                    ) {
                                        Icon(Icons.Default.SmartToy, null)
                                        Spacer(Modifier.width(8.dp))
                                        Text("Consultar con IA")
                                    }
                                }
                            }
                        }

                        items(resultadosFiltrados, key = { "${it.codigo}_${it.nombre}" }) { item ->
                            CardSellado(
                                navController = navController,
                                item = item,
                                expandido = expandidosSet.contains(item.codigo),
                                onToggleExpand = {
                                    expandidosSet = if (expandidosSet.contains(item.codigo))
                                        expandidosSet - item.codigo
                                    else
                                        expandidosSet + item.codigo
                                },
                                onSugerenciaClick = { cod ->
                                    busqueda = cod
                                    expandidosSet = setOf(cod)
                                }
                            )
                        }
                        
                        item {
                            Spacer(Modifier.height(16.dp))
                            BotonSoporte()
                        }
                    }
                }
            }
        }
    }
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun CardSellado(
    navController: NavController,
    item: SelladoItem,
    expandido: Boolean,
    onToggleExpand: () -> Unit,
    onSugerenciaClick: (String) -> Unit
) {
    var montoInput by rememberSaveable(item.codigo) { mutableStateOf("") }
    var fojasCount by rememberSaveable(item.codigo + "_fojas_count") { mutableStateOf(0) }
    var tieneGarante by rememberSaveable(item.codigo + "_garante") { mutableStateOf(false) }

    // --- ESTADOS PARA DESDOBLAMIENTO DE ALQUILERES ---
    var modoIngresoTotal by rememberSaveable(item.codigo + "_modo_alquiler") { mutableStateOf(true) }
    var canonMensualInput by rememberSaveable(item.codigo + "_canon") { mutableStateOf("") }
    var plazoMesesInput by rememberSaveable(item.codigo + "_plazo") { mutableStateOf("") }

    // --- NUEVOS ESTADOS FISCALES Y BIMONETARIOS ---
    var valuacionFiscalInput by rememberSaveable(item.codigo + "_valuacion") { mutableStateOf("") }
    var esMonedaExtranjera by rememberSaveable(item.codigo + "_usd_switch") { mutableStateOf(false) }
    var cotizacionOficialInput by rememberSaveable(item.codigo + "_cotiz") { mutableStateOf("") }

    var calculoDetalle by remember { mutableStateOf<CalculoDetalle?>(null) }
    var resultVisible by remember { mutableStateOf(false) }
    var showResult by remember { mutableStateOf(false) }

    // Detección de tipos de acto para habilitar opciones específicas
    val esLocacion = remember(item.nombre) {
        item.nombre.contains("locación", ignoreCase = true) ||
        item.nombre.contains("alquiler", ignoreCase = true)
    }
    val esCompraventaOTransferencia = remember(item.nombre) {
        item.nombre.contains("compraventa", ignoreCase = true) ||
        item.nombre.contains("transferencia", ignoreCase = true)
    }

    // LIMPIEZA DE ESTADOS AL CAMBIAR MODOS (Evita datos fantasma)
    LaunchedEffect(esMonedaExtranjera) {
        if (!esMonedaExtranjera) cotizacionOficialInput = ""
        showResult = false
    }

    // --- EFECTO DE RESULTADO CON DELAY ---
    LaunchedEffect(showResult) {
        if (showResult) {
            delay(400)
            resultVisible = true
        } else {
            resultVisible = false
        }
    }

    val valorMT = SelladosRepository.getValorMT()

    // LÓGICA DINÁMICA DE MONTO BASE (PRE-MOTOR)
    val montoBase = remember(montoInput, canonMensualInput, plazoMesesInput, valuacionFiscalInput, esMonedaExtranjera, cotizacionOficialInput, modoIngresoTotal) {
        fun String.toCleanDouble() = this.replace(".", "").replace(",", ".").toDoubleOrNull() ?: 0.0
        
        val cotiz = if (esMonedaExtranjera) cotizacionOficialInput.toCleanDouble() else 1.0
        
        when {
            // Caso 1: Alquiler Desdoblado
            esLocacion && !modoIngresoTotal -> {
                val canon = canonMensualInput.toCleanDouble()
                val plazo = plazoMesesInput.toIntOrNull() ?: 0
                canon * plazo
            }
            
            // Caso 2: Compraventa / Transferencia (Comparador de Mayor Valor)
            esCompraventaOTransferencia -> {
                val precioPactadoPesos = montoInput.toCleanDouble() * cotiz
                val valuacionFiscal = valuacionFiscalInput.toCleanDouble()
                maxOf(precioPactadoPesos, valuacionFiscal)
            }
            
            // Caso 3: General (Monto simple o Moneda Extranjera)
            else -> {
                montoInput.toCleanDouble() * cotiz
            }
        }
    }

    val rotation by animateFloatAsState(if (expandido) 180f else 0f)
    val recomendacion = remember(item.codigo) { SelladosRepository.getRecomendaciones(item.codigo) }

    Card(
        modifier = Modifier.fillMaxWidth().clip(RoundedCornerShape(16.dp)).clickable { onToggleExpand() },
        shape = RoundedCornerShape(16.dp),
        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
        border = if (expandido) BorderStroke(2.dp, MaterialTheme.colorScheme.primary.copy(alpha = 0.5f)) else BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant)
    ) {
        Column(modifier = Modifier.padding(16.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Surface(
                    color = MaterialTheme.colorScheme.primaryContainer,
                    shape = RoundedCornerShape(4.dp)
                ) {
                    Text(
                        item.codigo,
                        modifier = Modifier.padding(horizontal = 6.dp, vertical = 2.dp),
                        style = MaterialTheme.typography.labelSmall,
                        fontWeight = FontWeight.Bold,
                        color = MaterialTheme.colorScheme.onPrimaryContainer
                    )
                }
                Spacer(Modifier.width(8.dp))
                BadgeTipo(item.tipo)
                Spacer(modifier = Modifier.weight(1f))
                Icon(
                    Icons.Default.ExpandMore,
                    contentDescription = null,
                    modifier = Modifier.rotate(rotation),
                    tint = MaterialTheme.colorScheme.primary
                )
            }

            Spacer(Modifier.height(8.dp))
            Text(
                text = item.nombre,
                fontWeight = FontWeight.Bold,
                style = MaterialTheme.typography.titleMedium,
                color = MaterialTheme.colorScheme.onSurface
            )

            AnimatedVisibility(visible = expandido) {
                Column(modifier = Modifier.padding(top = 16.dp)) {
                    HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant, thickness = 0.5.dp)
                    Spacer(Modifier.height(12.dp))
                    
                    InfoRow("Alícuota", item.aliquotaStr)
                    InfoRow("Mínimos", item.minimoStr)
                    if (item.maximoStr != "0" && item.maximoStr.isNotEmpty()) {
                        InfoRow("Máximo", item.maximoStr)
                    }
                    InfoRow("Exento", item.exento)
                    InfoRow("Adicionales", item.adicionales)
                    InfoRow("Fojas", item.fojas)
                    InfoRow("Norma Legal", item.norma)
                    
                    if (item.observaciones.isNotEmpty()) {
                        Text(
                            text = item.observaciones,
                            style = MaterialTheme.typography.bodySmall,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                            modifier = Modifier
                                .padding(vertical = 8.dp)
                                .background(MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.3f), RoundedCornerShape(8.dp))
                                .padding(8.dp)
                        )
                    }

                    // SECCIÓN DE RECOMENDACIONES PROFESIONALES
                    recomendacion?.let { rec ->
                        CardRecomendacion(rec, onSugerenciaClick)
                    }

                    Spacer(Modifier.height(16.dp))
                    
                    if (item.tipo != TipoSellado.EXENTO) {
                        if (item.tipo == TipoSellado.POR_MONTO) {
                            // 1. SELECTOR DE MODALIDAD (Solo Locaciones)
                            if (esLocacion) {
                                Row(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .padding(bottom = 12.dp)
                                        .background(MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.3f), RoundedCornerShape(8.dp))
                                        .padding(4.dp),
                                    horizontalArrangement = Arrangement.spacedBy(4.dp)
                                ) {
                                    val colorTotal = if (modoIngresoTotal) MaterialTheme.colorScheme.primary else Color.Transparent
                                    val contentTotal = if (modoIngresoTotal) MaterialTheme.colorScheme.onPrimary else MaterialTheme.colorScheme.onSurfaceVariant
                                    
                                    Surface(
                                        modifier = Modifier.weight(1f).clickable { 
                                            modoIngresoTotal = true
                                            canonMensualInput = ""; plazoMesesInput = ""; showResult = false 
                                        },
                                        color = colorTotal,
                                        shape = RoundedCornerShape(6.dp)
                                    ) {
                                        Text("Ingresar Total", modifier = Modifier.padding(8.dp), textAlign = TextAlign.Center, style = MaterialTheme.typography.labelSmall, fontWeight = FontWeight.Bold, color = contentTotal)
                                    }

                                    val colorMeses = if (!modoIngresoTotal) MaterialTheme.colorScheme.primary else Color.Transparent
                                    val contentMeses = if (!modoIngresoTotal) MaterialTheme.colorScheme.onPrimary else MaterialTheme.colorScheme.onSurfaceVariant
                                    
                                    Surface(
                                        modifier = Modifier.weight(1f).clickable { 
                                            modoIngresoTotal = false
                                            montoInput = ""; showResult = false 
                                        },
                                        color = colorMeses,
                                        shape = RoundedCornerShape(6.dp)
                                    ) {
                                        Text("Calcular por Meses", modifier = Modifier.padding(8.dp), textAlign = TextAlign.Center, style = MaterialTheme.typography.labelSmall, fontWeight = FontWeight.Bold, color = contentMeses)
                                    }
                                }
                            }

                            // 2. CONVERSOR BIMONETARIO (USD)
                            if (!(esLocacion && !modoIngresoTotal)) {
                                Row(
                                    modifier = Modifier.fillMaxWidth().padding(bottom = 12.dp)
                                        .clickable { esMonedaExtranjera = !esMonedaExtranjera },
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    Switch(
                                        checked = esMonedaExtranjera,
                                        onCheckedChange = { esMonedaExtranjera = it },
                                        thumbContent = if (esMonedaExtranjera) { { Icon(Icons.Default.AttachMoney, null, modifier = Modifier.size(16.dp)) } } else null
                                    )
                                    Spacer(Modifier.width(12.dp))
                                    Text("Contrato en Moneda Extranjera (USD)", style = MaterialTheme.typography.bodySmall, fontWeight = FontWeight.Bold)
                                }
                            }

                            // 3. CAMPOS DE ENTRADA DINÁMICOS
                            if (esLocacion && !modoIngresoTotal) {
                                Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                                    OutlinedTextField(
                                        value = canonMensualInput,
                                        onValueChange = { canonMensualInput = it; showResult = false },
                                        label = { Text("Canon ($)") },
                                        modifier = Modifier.weight(1.5f),
                                        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Decimal),
                                        shape = RoundedCornerShape(12.dp)
                                    )
                                    OutlinedTextField(
                                        value = plazoMesesInput,
                                        onValueChange = { if (it.all { c -> c.isDigit() }) plazoMesesInput = it; showResult = false },
                                        label = { Text("Meses") },
                                        modifier = Modifier.weight(1f),
                                        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                                        shape = RoundedCornerShape(12.dp)
                                    )
                                }
                            } else {
                                Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                                    val labelMonto = if (esMonedaExtranjera) "Monto en USD" else if (esCompraventaOTransferencia) "Precio de Venta ($)" else "Monto imponible ($)"
                                    
                                    OutlinedTextField(
                                        value = montoInput,
                                        onValueChange = { montoInput = it; showResult = false },
                                        label = { Text(labelMonto) },
                                        modifier = Modifier.fillMaxWidth(),
                                        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Decimal),
                                        leadingIcon = { Icon(if (esMonedaExtranjera) Icons.Default.AttachMoney else Icons.Default.Payments, null) },
                                        shape = RoundedCornerShape(12.dp)
                                    )

                                    if (esMonedaExtranjera) {
                                        OutlinedTextField(
                                            value = cotizacionOficialInput,
                                            onValueChange = { cotizacionOficialInput = it; showResult = false },
                                            label = { Text("Cotización Oficial ($)") },
                                            placeholder = { Text("Ej: 1050.50") },
                                            modifier = Modifier.fillMaxWidth(),
                                            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Decimal),
                                            leadingIcon = { Icon(Icons.Default.CurrencyExchange, null) },
                                            shape = RoundedCornerShape(12.dp),
                                            colors = OutlinedTextFieldDefaults.colors(focusedContainerColor = MaterialTheme.colorScheme.primaryContainer.copy(alpha = 0.1f))
                                        )
                                    }

                                    if (esCompraventaOTransferencia) {
                                        OutlinedTextField(
                                            value = valuacionFiscalInput,
                                            onValueChange = { valuacionFiscalInput = it; showResult = false },
                                            label = { Text("Valuación Fiscal ($)") },
                                            modifier = Modifier.fillMaxWidth(),
                                            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Decimal),
                                            leadingIcon = { Icon(Icons.Default.AccountBalance, null) },
                                            shape = RoundedCornerShape(12.dp)
                                        )
                                    }
                                }
                            }

                            if (esLocacion) {
                                Row(
                                    modifier = Modifier.fillMaxWidth().padding(top = 8.dp)
                                        .clickable { tieneGarante = !tieneGarante; showResult = false },
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    Checkbox(
                                        checked = tieneGarante,
                                        onCheckedChange = { tieneGarante = it; showResult = false }
                                    )
                                    Text(
                                        "Incluye Garante / Fiador (Agrega 5‰ por Fianza)",
                                        style = MaterialTheme.typography.bodySmall,
                                        fontWeight = FontWeight.Medium
                                    )
                                }
                            }
                        }

                        if (item.fojas != "NO" && item.fojas.isNotEmpty()) {
                            Spacer(Modifier.height(12.dp))
                            Text("Cantidad de fojas adicionales", style = MaterialTheme.typography.labelMedium, color = MaterialTheme.colorScheme.primary)
                            Row(
                                modifier = Modifier.fillMaxWidth().padding(vertical = 4.dp),
                                verticalAlignment = Alignment.CenterVertically,
                                horizontalArrangement = Arrangement.spacedBy(16.dp)
                            ) {
                                Surface(
                                    shape = RoundedCornerShape(12.dp),
                                    border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
                                    color = MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.2f)
                                ) {
                                    Row(verticalAlignment = Alignment.CenterVertically) {
                                        IconButton(onClick = { if (fojasCount > 0) { fojasCount--; showResult = false } }) {
                                            Icon(Icons.Default.Remove, "Quitar", tint = MaterialTheme.colorScheme.primary)
                                        }
                                        Text(
                                            fojasCount.toString(),
                                            modifier = Modifier.width(40.dp),
                                            textAlign = TextAlign.Center,
                                            fontWeight = FontWeight.Bold,
                                            style = MaterialTheme.typography.titleMedium
                                        )
                                        IconButton(onClick = { fojasCount++; showResult = false }) {
                                            Icon(Icons.Default.Add, "Agregar", tint = MaterialTheme.colorScheme.primary)
                                        }
                                    }
                                }
                                Text("Copias u hojas extra", style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.onSurfaceVariant)
                            }
                        }

                        CalculatorActionLock(
                            navController = navController,
                            onAction = {
                                calculoDetalle = SelladosEngine.realizarCalculoIntegral(
                                    item, montoBase, fojasCount, valorMT, tieneGarante
                                )
                                showResult = true
                            }
                        ) { onClick ->
                            Button(
                                onClick = onClick,
                                modifier = Modifier.fillMaxWidth().padding(top = 12.dp),
                                shape = RoundedCornerShape(12.dp),
                                enabled = item.tipo == TipoSellado.FIJO_MT || montoBase > 0 || (item.fojas != "NO" && fojasCount > 0)
                            ) {
                                Icon(Icons.Default.Calculate, null, modifier = Modifier.size(18.dp))
                                Spacer(Modifier.width(8.dp))
                                Text("Calcular Boleta")
                            }
                        }

                        if (showResult && calculoDetalle != null) {
                            ResultPanelIntegral(detalle = calculoDetalle!!)
                        }
                    } else {
                        InfoBox("Este acto se encuentra exento según ${item.norma}.", Color(0xFF2E7D32), Icons.Default.CheckCircle)
                    }
                }
            }
        }
    }
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun CardRecomendacion(recomendacion: RecomendacionSellado, onCodigoClick: (String) -> Unit) {
    Card(
        modifier = Modifier.fillMaxWidth().padding(top = 12.dp),
        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.tertiaryContainer.copy(alpha = 0.15f)),
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.tertiary.copy(alpha = 0.2f))
    ) {
        Column(modifier = Modifier.padding(12.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Icon(Icons.Default.Info, null, tint = MaterialTheme.colorScheme.tertiary, modifier = Modifier.size(18.dp))
                Spacer(Modifier.width(8.dp))
                Text(
                    "INFORMACIÓN COMPLEMENTARIA: ${recomendacion.titulo}",
                    style = MaterialTheme.typography.labelSmall,
                    fontWeight = FontWeight.Bold,
                    color = MaterialTheme.colorScheme.tertiary
                )
            }
            Spacer(Modifier.height(6.dp))
            Text(
                recomendacion.descripcion,
                style = MaterialTheme.typography.bodySmall,
                fontSize = 11.sp,
                lineHeight = 14.sp,
                color = MaterialTheme.colorScheme.onTertiaryContainer
            )
            Spacer(Modifier.height(10.dp))
            Text("Combinaciones y sellados habituales:", style = MaterialTheme.typography.labelSmall, fontWeight = FontWeight.SemiBold)
            Spacer(Modifier.height(4.dp))
            FlowRow(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                recomendacion.codigosRelacionados.forEach { cod ->
                    SuggestionChip(
                        onClick = { onCodigoClick(cod) },
                        label = { Text("Cód. $cod", fontSize = 10.sp) },
                        icon = { Icon(Icons.Default.Add, null, Modifier.size(14.dp)) },
                        colors = SuggestionChipDefaults.suggestionChipColors(labelColor = MaterialTheme.colorScheme.tertiary)
                    )
                }
            }
        }
    }
}

@Composable
private fun InfoRow(label: String, value: String) {
    if (value.isEmpty() || value == "NO" || value == "0") return
    Row(modifier = Modifier.padding(vertical = 2.dp)) {
        Text("$label: ", style = MaterialTheme.typography.labelMedium, fontWeight = FontWeight.Bold, color = MaterialTheme.colorScheme.primary)
        Text(value, style = MaterialTheme.typography.bodySmall)
    }
}

@Composable
private fun BadgeTipo(tipo: TipoSellado) {
    val (color, text) = when (tipo) {
        TipoSellado.POR_MONTO -> MaterialTheme.colorScheme.secondary to "Proporcional"
        TipoSellado.FIJO_MT -> Color(0xFFE64A19) to "Monto Fijo"
        TipoSellado.EXENTO -> Color(0xFF2E7D32) to "Exento"
    }

    Surface(
        color = color.copy(alpha = 0.1f),
        shape = RoundedCornerShape(6.dp),
        border = BorderStroke(1.dp, color.copy(alpha = 0.3f))
    ) {
        Text(
            text = text,
            modifier = Modifier.padding(horizontal = 6.dp, vertical = 2.dp),
            style = MaterialTheme.typography.labelSmall,
            fontWeight = FontWeight.Bold,
            color = color
        )
    }
}

@Composable
private fun ResultPanelIntegral(detalle: CalculoDetalle) {
    val textoCopia = buildString {
        append("DETALLE DE LIQUIDACIÓN SELLADOS\n")
        append("${detalle.labelPrincipal}: ${detalle.montoPrincipal.toPesos()}\n")
        if (detalle.montoFianza > 0) append("Fianza / Garantía (5‰): ${detalle.montoFianza.toPesos()}\n")
        if (detalle.montoAdicional > 0) append("${detalle.labelAdicional}: ${detalle.montoAdicional.toPesos()}\n")
        if (detalle.montoReposicion > 0) append("${detalle.labelReposicion}: ${detalle.montoReposicion.toPesos()}\n")
        if (detalle.montoFojas > 0) append("Fojas adicionales (${detalle.cantFojas}): ${detalle.montoFojas.toPesos()}\n")
        append("-------------------------------------------\n")
        append("TOTAL A PAGAR: ${detalle.total.toPesos()}\n")
        append("-------------------------------------------\n")
        append("Calculado con www.juridicorosario.com")
    }

    CajaResultadoAccionable(
        titulo = "Resultado de Liquidación",
        textoParaCompartir = textoCopia,
        colorFondo = MaterialTheme.colorScheme.primaryContainer.copy(alpha = 0.15f)
    ) {
        Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
            ResultRow(label = detalle.labelPrincipal, amount = detalle.montoPrincipal)

            if (detalle.montoFianza > 0) {
                ResultRow(label = "Fianza / Garantía (5‰)", amount = detalle.montoFianza)
            }

            if (detalle.montoAdicional > 0) {
                ResultRow(label = detalle.labelAdicional, amount = detalle.montoAdicional)
            }

            if (detalle.montoReposicion > 0) {
                ResultRow(label = detalle.labelReposicion, amount = detalle.montoReposicion)
            }

            if (detalle.montoFojas > 0) {
                ResultRow(label = "Fojas adicionales (${detalle.cantFojas})", amount = detalle.montoFojas)
            }

            HorizontalDivider(Modifier.padding(vertical = 8.dp), thickness = 1.dp, color = MaterialTheme.colorScheme.primary.copy(alpha = 0.1f))

            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                Text("TOTAL A PAGAR", style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Black)
                Text(
                    detalle.total.toPesos(),
                    style = MaterialTheme.typography.headlineSmall,
                    fontWeight = FontWeight.Black,
                    color = MaterialTheme.colorScheme.primary
                )
            }
        }
    }
}

@Composable
private fun ResultRow(label: String, amount: Double) {
    Row(Modifier.fillMaxWidth().padding(vertical = 2.dp), horizontalArrangement = Arrangement.SpaceBetween) {
        Text(label, style = MaterialTheme.typography.bodySmall, modifier = Modifier.weight(1f), maxLines = 1, overflow = TextOverflow.Ellipsis)
        Text(amount.toPesos(), style = MaterialTheme.typography.bodySmall, fontWeight = FontWeight.Bold)
    }
}

@Composable
private fun InfoBox(message: String, color: Color, icon: ImageVector) {
    Surface(
        color = color.copy(alpha = 0.05f),
        shape = RoundedCornerShape(12.dp),
        border = BorderStroke(1.dp, color.copy(alpha = 0.2f)),
        modifier = Modifier.fillMaxWidth().padding(vertical = 8.dp)
    ) {
        Row(Modifier.padding(12.dp), verticalAlignment = Alignment.CenterVertically) {
            Icon(icon, null, tint = color, modifier = Modifier.size(20.dp))
            Spacer(Modifier.width(12.dp))
            Text(message, style = MaterialTheme.typography.bodySmall, color = color, fontWeight = FontWeight.Medium)
        }
    }
}

@Composable
private fun BotonSoporte() {
    OutlinedButton(
        onClick = { openUrl("tel:03414292138") },
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(12.dp)
    ) {
        Icon(Icons.AutoMirrored.Filled.Help, null)
        Spacer(Modifier.width(8.dp))
        Text("CONSULTAS API / SELLADOS", fontWeight = FontWeight.Bold)
    }
}

@Composable
private fun PantallaErrorCarga() {
    Column(
        modifier = Modifier.fillMaxSize().padding(32.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.Center
    ) {
        Icon(Icons.Default.Warning, null, modifier = Modifier.size(64.dp), tint = MaterialTheme.colorScheme.error)
        Spacer(Modifier.height(16.dp))
        Text("Error de base de datos", style = MaterialTheme.typography.titleMedium)
        Text("No se pudo leer el nomenclador oficial.", textAlign = TextAlign.Center)
    }
}
